import { merge } from 'lodash';
import React, { useMemo, useCallback } from 'react';

import { ProjectSortFields, SortDirection, GetProjectsQueryVariables } from '@/graphql';
import { BaseTableProps } from '@boilerplate/types/entity';
import Entity from '@/entities/project';
import EntityTable, {
  defaultPage,
  defaultPageSize,
  defaultSortOrder,
  getDefaultSortField,
} from '@boilerplate/components/entity/EntityTable/EntityTable';
import { canPerformAction, useAuthenticatedUserStore } from '@/stores/UserStore';
import { openConfirmationDialog } from '@/stores/ConfirmationDialogStore';


export const useProjectsTable = ({ queryOptions }: { queryOptions?: GetProjectsQueryVariables } = {}) => {
  const defaultVariableOptions = useMemo<GetProjectsQueryVariables>(
    () => 
      merge(
        {
          sorting: {
            direction: defaultSortOrder === 'desc' ? SortDirection.Desc : SortDirection.Asc,
            field: getDefaultSortField(Entity.table.schema) as ProjectSortFields,
          },
          paging: {
            page: defaultPage,
            pageSize: defaultPageSize,
          },
                  withCustomer: true,
                  withTenant: true,
          },
          queryOptions,
        ), 
    [queryOptions]
    );
  
  const {
    data: projectData,
    items = [],
    loading,
    refetch: originalRefetch,
    variables,
  } = Entity.model.useGetAll({
    fetchPolicy: 'cache-and-network',
    variables: defaultVariableOptions,
  });

  const [remove] = Entity.model.useDelete();

  const refetch = useCallback(
    (variableOptions?: Partial<GetProjectsQueryVariables >) => {
      return originalRefetch(merge({}, defaultVariableOptions, variableOptions));
    },
    [defaultVariableOptions, originalRefetch]
  );

  const allActions = useMemo(() => ({ loading, refetch, remove }), [loading, refetch, remove]);
  const data = useMemo(
    () =>
      projectData
        ? { totalCount: projectData.projects.totalCount, rows: items }
        : { totalCount: 0, rows: [] },
    [projectData, items]
  );

  return { data, actions: allActions, refetch, defaultVariableOptions, variables };
}

export type ProjectsBaseTableProps = BaseTableProps<typeof Entity.model>;

export default function ProjectsBaseTable(props: ProjectsBaseTableProps) {
  const tableProps = useProjectsTable();
  const authentictedUser = useAuthenticatedUserStore();

  return (
    <EntityTable
      {...props}
      {...tableProps}
      Entity={Entity}
      user={authentictedUser}
      canPerformThisAction={canPerformAction}
      openConfirmationDialog={openConfirmationDialog}
    />
  );
}
